// Explore: a board on which any moves can be played, for either side, and
// under it everything that helps to go on learning from the position: the
// gambit the moves are in, how the line goes on at your level — the book's
// move, what is played, where the side to move most often goes wrong — all
// of it on the board as arrows to play, and the gambits still ahead, with how
// tricky each is. There is always a next move to look at. OPENINGS.md §5.4.

import { useEffect, useMemo, useRef, useState } from "react";
import { Chess } from "chess.js";
import { errorText, getJson, isSignedOut } from "../app/api.ts";
import { scoreOf } from "../app/engine.ts";
import { bandLabel, fmtScore, pct, plyLabel } from "../app/format.ts";
import { Icon } from "../app/icons.tsx";
import { humanTag } from "../app/kinds.ts";
import { goBack, navigate, setAddress } from "../app/router.ts";
import { BackButton, type ScreenProps } from "../app/App.tsx";
import { Board, type Reply } from "../board/Board.tsx";
import { BookPanel, type BookPosition } from "../board/BookPanel.tsx";
import { standardLine, type Score } from "../board/lines.ts";
import { ScoreStrip } from "../board/PuzzleBoard.tsx";
import { winChances } from "../board/scores.ts";
import { quirks, quirkSays, quirkTitle, type Quirk } from "../board/sly.ts";
import { BoardMenu, BoardScreen } from "./BoardScreen.tsx";
import "./repertoire.css";

interface Combination {
  sans: string[];
  motifs: string[];
  wins: { kind: "mate" } | { kind: "material"; cents: number } | { kind: "position" };
}

interface Gambit {
  name: string;
  eco: string;
  side: "white" | "black";
  sans: string[];
  ucis: string[];
  from_ply: number;
  reach: number;
  score: number;
  games: number;
  white_eval: Score | null;
  tricky: number;
  trap: { san: string; uci: string; share: number; combination: Combination | null } | null;
}

interface Answer {
  fen: string;
  name: string | null;
  /** The gambit the moves are in: the last one named along them. */
  within: { name: string; side: "white" | "black"; ply: number; white_eval: Score | null; score: number | null } | null;
  /** The last move, when it was a mistake, and what punishes it. */
  went_wrong: { san: string; side: "white" | "black"; ply: number; lost: number; combination: Combination | null } | null;
  band: string;
  gambits: Gambit[];
}

const START = new Chess().fen();
const sameFen = (a: string, b: string) => a.split(" ").slice(0, 4).join(" ") === b.split(" ").slice(0, 4).join(" ");
const colour = (side: string) => (side === "white" ? "White" : "Black");

/** The moves, as far along them as the board is, replayed; those that do not
 *  play are dropped. */
function replay(moves: string[]): { fen: string; sans: string[]; ucis: string[] } {
  const game = new Chess();
  const ucis: string[] = [];
  for (const uci of moves) {
    try {
      if (!game.move({ from: uci.slice(0, 2), to: uci.slice(2, 4), promotion: uci.slice(4, 5) || "q" })) break;
      ucis.push(uci);
    } catch {
      break;
    }
  }
  return { fen: game.fen(), sans: game.history(), ucis };
}

/** Moves in SAN with their numbers, from the ply the first is played at. */
function numbered(sans: string[], fromPly: number): string {
  return sans.map((san, i) => ((fromPly + i) % 2 === 0 || i === 0 ? `${plyLabel(fromPly + i)} ${san}` : san)).join(" ");
}

/** What the combination after a trap wins, in words. */
function wins(c: Combination): string {
  if (c.wins.kind === "mate") return "mates";
  if (c.wins.kind === "material") {
    const cents = c.wins.cents;
    return cents >= 900 ? "wins the queen's worth" : cents >= 500 ? "wins a rook's worth" : cents >= 300 ? "wins a piece" : cents >= 200 ? "wins two pawns" : "wins a pawn";
  }
  return "wins the position";
}

export function ExploreScreen({ query }: ScreenProps) {
  // The moves on the board, and how many of them are played: ← and → walk
  // them without forgetting the ones stepped back from.
  const [moves, setMoves] = useState<string[]>(() => replay((query.get("moves") || "").split(",").filter(Boolean)).ucis);
  const [at, setAt] = useState(moves.length);
  const [flipped, setFlipped] = useState(false);
  const [answer, setAnswer] = useState<{ fen: string; data?: Answer; error?: string } | null>(null);
  const [book, setBook] = useState<{ fen: string; position: BookPosition; band: string } | null>(null);
  const [engine, setEngine] = useState<{ fen: string; score: Score | null } | null>(null);
  const shown = useMemo(() => replay(moves.slice(0, at)), [moves, at]);
  const meta = { title: "Explore", nav: "openings", back: "/openings", backLabel: "Openings" };

  // The moves are in the address: a reload, or Back from what was opened
  // from here, comes back to the position.
  useEffect(() => setAddress({ moves: shown.ucis.join(",") }), [shown]);

  // What is ahead of the position, asked once the board has stayed on it.
  const asked = useRef("");
  useEffect(() => {
    const fen = shown.fen;
    asked.current = fen;
    const timer = setTimeout(() => {
      getJson<Answer>(`/api/openings/gambits?moves=${shown.ucis.join(",")}`)
        .then((data) => asked.current === fen && setAnswer({ fen, data }))
        .catch((err) => !isSignedOut(err) && asked.current === fen && setAnswer({ fen, error: errorText(err) }));
    }, 300);
    return () => clearTimeout(timer);
  }, [shown.fen]); // eslint-disable-line react-hooks/exhaustive-deps

  const here = book && sameFen(book.fen, shown.fen) ? book : null;
  // What the position is worth, from White's side: the book's, else the
  // engine's, asked once the board has stayed a moment.
  const bookScore = here?.position.white_eval || null;
  useEffect(() => {
    if (bookScore) return;
    const fen = shown.fen;
    let live = true;
    const timer = setTimeout(() => void scoreOf(fen).then((score) => live && setEngine({ fen, score })), 400);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [shown.fen, Boolean(bookScore)]); // eslint-disable-line react-hooks/exhaustive-deps
  const score = bookScore || (engine && sameFen(engine.fen, shown.fen) ? engine.score : null);

  const legal = useMemo(() => {
    try {
      return new Chess(shown.fen).moves({ verbose: true }).map((m) => m.from + m.to + (m.promotion || ""));
    } catch {
      return [];
    }
  }, [shown.fen]);
  // The book writes castling as the king taking the rook; the board as its
  // two steps.
  const asBoard = (uci: string) => standardLine(shown.fen, [uci])[0] || uci;
  const play = (uci: string) => {
    const move = asBoard(uci);
    setMoves((was) => (was[at] === move ? was : [...was.slice(0, at), move]));
    setAt(at + 1);
  };
  const showLine = (ucis: string[]) => {
    const next = [...moves.slice(0, at), ...ucis];
    setMoves(next);
    setAt(next.length);
  };

  const current = answer && sameFen(answer.fen, shown.fen) ? answer : null;
  const gambits = current?.data?.gambits || [];
  const toMove = shown.fen.split(" ")[1] === "b" ? "black" : "white";
  // Where the side to move goes wrong, and what is rare and scores for it:
  // by what the moves score, where enough games say so, and by what the
  // engine makes of them, which needs no number of games — 3…g6? after the
  // Carrera is a fifth of the replies and loses a rook to 4.Qe5+.
  const counted = here ? quirks(here.position, "trap") : [];
  const wrong = [...counted, ...(here ? losing(here.position, counted) : [])];
  const rare = here ? quirks(here.position, "sly") : [];
  const recommended = here?.position.recommended || [];

  // On the board, as arrows to play: the book's move, the side to move's
  // common mistake and its rare good move, and the first moves of the gambits
  // it can play. One arrow a move.
  const arrows = useMemo(() => {
    const out = new Map<string, Reply>();
    const put = (uci: string, label: string, tone: Reply["tone"]) => {
      const key = asBoard(uci);
      if (!out.has(key) && out.size < 5) out.set(key, { uci: key, label, tone });
    };
    for (const r of recommended.slice(0, 2)) put(r.uci, r.san, "line");
    for (const q of wrong) put(q.uci, q.san, "trap");
    for (const q of rare) put(q.uci, q.san, "sly");
    for (const g of gambits.filter((g) => g.side === toMove)) put(g.ucis[0]!, g.sans[0]!, "line");
    return [...out.values()];
  }, [recommended, wrong, rare, gambits, toMove]); // eslint-disable-line react-hooks/exhaustive-deps

  const last = shown.ucis.length ? shown.ucis[shown.ucis.length - 1]! : null;
  const ply = shown.sans.length;
  const within = current?.data?.within || null;
  const band = current?.data?.band || here?.band || "";
  const sides: ("white" | "black")[] = toMove === "white" ? ["white", "black"] : ["black", "white"];

  return (
    <div className="screen explore-screen">
      <BackButton meta={meta} />
      <BoardScreen
        className="rep-board-screen"
        board={
          <div className="board-zone">
            <Board
              className="view-board"
              fen={shown.fen}
              orientation={flipped ? "black" : "white"}
              last={last}
              replies={arrows}
              tone="line"
              onReply={play}
              input={{ legal, onMove: play }}
            />
            <ScoreStrip score={{ whiteShare: Math.round((winChances(score) + 1) * 50), text: score ? fmtScore(score) : "…" }} />
          </div>
        }
        line={
          <div className="rep-caption">
            <div className="rep-caption-head">
              <button type="button" className="mv-chip step step-back" title="One move back" aria-label="One move back" disabled={at === 0} onClick={() => setAt(at - 1)}>
                <Icon name="stepBack" />
              </button>
              <span className="rep-caption-mid">
                <span className="rep-caption-move">
                  <b>{ply ? `${plyLabel(ply - 1)} ${shown.sans[ply - 1]}` : "Start"}</b>
                </span>
              </span>
              <button type="button" className="mv-chip step step-on" title="On a move" aria-label="On a move" disabled={at >= moves.length} onClick={() => setAt(at + 1)}>
                <Icon name="stepOn" />
              </button>
            </div>
          </div>
        }
        bar={
          <>
            <button type="button" className="btn menu-btn" onClick={() => goBack(meta.back)}>
              <Icon name="back" />
              Back
            </button>
            <button type="button" className="btn" disabled={!moves.length} onClick={() => (setMoves([]), setAt(0))}>
              <Icon name="stepBack" />
              Start over
            </button>
            <BoardMenu
              shown={() => ({ fen: shown.fen, start: START, moves: shown.sans, headers: { Event: "Blundermine — Explore", Site: location.href } })}
              items={[{ label: "Flip the board", onClick: () => setFlipped((f) => !f) }]}
            />
          </>
        }
        sheet={
          <>
            <h2 className="puzzle-title">{current?.data?.name || within?.name || (ply ? "Off the named lines" : "Any opening")}</h2>
            <p className="puzzle-evals drill-path">{ply ? numbered(shown.sans, 0) : "Play moves for either side, or tap an arrow."}</p>

            {current?.data?.went_wrong ? <WentWrong w={current.data.went_wrong} onShow={(ucis) => showLine(ucis)} fen={shown.fen} /> : null}
            {within ? <Within within={within} /> : null}

            <h3 className="explore-head">How it goes on · {colour(toMove)} to move</h3>
            {wrong.length || rare.length ? (
              <div className="explore-quirks">
                {[...wrong, ...rare].map((q) => (
                  <QuirkRow key={q.kind + q.uci} q={q} ply={ply} side={toMove} onPlay={() => play(q.uci)} />
                ))}
              </div>
            ) : null}
            <BookPanel fen={shown.fen} onData={(position, b) => setBook({ fen: position.fen || shown.fen, position, band: b })} />
            {here && !here.position.in_book ? (
              <p className="faint explore-note">Few games reach this position at any level: the evaluation is the engine's, and the moves from here are yours to try.</p>
            ) : null}

            <h3 className="explore-head">Gambits from here</h3>
            {!current ? (
              <p className="faint">Looking at what is ahead…</p>
            ) : current.error ? (
              <div className="notice error">{current.error}</div>
            ) : !gambits.length ? (
              <p className="faint">
                {within ? `No new gambit starts from here: you are in the ${within.name.split(": ").pop()}, and the book above says how it goes on.` : "No new gambit starts from here that is played and scores at your level. The book above says how the game goes on."}
              </p>
            ) : (
              sides.map((side) =>
                gambits.some((g) => g.side === side) ? (
                  <div key={side} className="explore-side">
                    <div className="explore-side-head">
                      For {colour(side)}
                      {side === toMove ? ", to move" : ""}
                    </div>
                    {gambits
                      .filter((g) => g.side === side)
                      .map((g) => (
                        <GambitRow key={g.name} g={g} band={band} onShow={() => showLine(g.ucis)} />
                      ))}
                  </div>
                ) : null
              )
            )}
          </>
        }
      />
    </div>
  );
}

/** A mistake by the engine's verdict: a reply that at least 5% of games at
 *  your level play, and that loses at least a quarter of the game against the
 *  best reply — the measure a course calls a trap. */
const LOSING_SHARE = 0.05;
const LOSING_DROP = 0.25;
function losing(position: BookPosition, already: Quirk[]): Quirk[] {
  const white = String(position.fen).split(/\s+/)[1] !== "b";
  const mine = (s?: Score | null) => (s ? (white ? 1 : -1) * winChances(s) : null);
  const moves = (position.moves || []).filter((m) => m.white_eval);
  const best = moves.reduce<{ m: (typeof moves)[number]; v: number } | null>((b, m) => {
    const v = mine(m.white_eval)!;
    return !b || v > b.v ? { m, v } : b;
  }, null);
  if (!best) return [];
  return moves
    .filter((m) => (m.band_share || 0) >= LOSING_SHARE && best.v - mine(m.white_eval)! >= LOSING_DROP && !already.some((q) => q.uci === m.uci))
    .sort((a, b) => (b.band_share || 0) - (a.band_share || 0))
    .slice(0, 2)
    .map((m) => ({
      uci: m.uci,
      san: m.san,
      pool: "band" as const,
      share: m.band_share || 0,
      score: 0,
      games: m.band?.games || 0,
      usual: best.m.san,
      usualScore: 0,
      edge: best.v - mine(m.white_eval)!,
      kind: "trap" as const,
      engine: { eval: m.white_eval!, best: best.m.white_eval! },
    }));
}

/** The gambit the moves are in, what it is worth, and the way to learn it. */
function Within({ within }: { within: NonNullable<Answer["within"]> }) {
  const [family, variation] = within.name.split(": ");
  // Its side's view of the engine's number: a gambit gives a pawn, so a
  // little under nothing is sound; a lot under is a trick.
  const own = within.white_eval && within.white_eval.kind === "cp" ? (within.side === "white" ? 1 : -1) * within.white_eval.value : null;
  const other = colour(within.side === "white" ? "black" : "white");
  const verdict =
    own === null
      ? null
      : own >= -40
        ? "sound: the pawn buys what it should"
        : own >= -100
          ? `dubious: it asks ${other} to know the answer`
          : `unsound: it works when ${other} goes wrong, so learn where they do`;
  return (
    <div className="explore-within">
      <div>
        <span className="badge trap">Gambit</span> You are in the <b>{variation || family}</b>
        {variation ? <span className="faint"> ({family})</span> : null}, {colour(within.side)}'s gambit.
      </div>
      {within.score !== null || verdict ? (
        <div className="explore-within-verdict">
          {within.score !== null ? `${colour(within.side)} scores ${pct(within.score)} after it at your level` : ""}
          {within.score !== null && verdict ? "; " : ""}
          {verdict && within.white_eval ? (
            <>
              the engine gives <b>{fmtScore(within.white_eval)}</b> — {verdict}
            </>
          ) : null}
          .
        </div>
      ) : null}
      <div className="explore-gambit-actions">
        <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate(`/openings/learn?line=${encodeURIComponent(within.name)}&as=${within.side}`)}>
          <Icon name="book" />
          Study it
        </button>
      </div>
    </div>
  );
}

/** A move the numbers single out: where the side to move goes wrong, or what
 *  is rare and scores for it. */
function QuirkRow({ q, ply, side, onPlay }: { q: Quirk; ply: number; side: string; onPlay: () => void }) {
  return (
    <div className={"explore-quirk " + q.kind}>
      <div className="explore-quirk-text">
        <span className="explore-quirk-kind">{q.kind === "trap" ? `Where ${colour(side)} goes wrong` : quirkTitle(q.kind)}</span>{" "}
        <b>
          {plyLabel(ply)} {q.san}
          {q.kind === "trap" ? "?" : "!?"}
        </b>{" "}
        — {"engine" in q ? engineSays(q as EngineQuirk, side === "white") : quirkSays(q, true)}
      </div>
      <button type="button" className="btn btn-sm" onClick={onPlay}>
        <Icon name="play" />
        Play it
      </button>
    </div>
  );
}

type EngineQuirk = Quirk & { engine: { eval: Score; best: Score } };
/** A move the engine calls a mistake: one that loses, or one that only
 *  gives back what the side had. */
function engineSays(q: EngineQuirk, white: boolean): string {
  const after = (white ? 1 : -1) * winChances(q.engine.eval);
  const what = after < -0.2 ? "the engine says it loses" : after < 0.2 ? "the engine says it throws the advantage away" : "the engine says it gives much of the advantage back";
  return `${pct(q.share)} play it at your level, and ${what}: ${fmtScore(q.engine.eval)}, against ${fmtScore(q.engine.best)} for ${q.usual}.`;
}

/** The last move was a mistake: said, with what punishes it, to play. */
function WentWrong({ w, fen, onShow }: { w: NonNullable<Answer["went_wrong"]>; fen: string; onShow: (ucis: string[]) => void }) {
  const combo = w.combination;
  // The combination's moves, from the position on the board, for Show.
  const ucis = (() => {
    if (!combo) return [];
    const game = new Chess(fen);
    const out: string[] = [];
    for (const san of combo.sans) {
      try {
        const m = game.move(san);
        if (!m) break;
        out.push(m.from + m.to + (m.promotion || ""));
      } catch {
        break;
      }
    }
    return out;
  })();
  const motif = combo?.motifs.find((m) => m !== "mate");
  return (
    <div className="explore-wrong">
      <div>
        <b>
          {colour(w.side)} went wrong: {plyLabel(w.ply)} {w.san}?
        </b>{" "}
        {combo ? (
          <>
            <b>{numbered(combo.sans, w.ply + 1)}</b> {wins(combo)}
            {motif ? ` — a ${humanTag(motif)}` : ""}.
          </>
        ) : (
          <>It gives away {pct(w.lost / 2)} of the game: find the move that shows why.</>
        )}
      </div>
      {ucis.length ? (
        <div className="explore-gambit-actions">
          <button type="button" className="btn btn-sm" onClick={() => onShow(ucis)}>
            <Icon name="play" />
            Show
          </button>
        </div>
      ) : null}
    </div>
  );
}

function GambitRow({ g, band, onShow }: { g: Gambit; band: string; onShow: () => void }) {
  const [family, variation] = g.name.split(": ");
  const trap = g.trap;
  const combo = trap?.combination;
  const motif = combo?.motifs.find((m) => m !== "mate");
  return (
    <div className="explore-gambit">
      <div className="explore-gambit-name">
        <b>{variation || family}</b>
        {variation ? <span className="faint"> {family}</span> : null}
      </div>
      <div className="explore-gambit-moves">{numbered(g.sans, g.from_ply)}</div>
      <div className="explore-gambit-stats">
        Reached in {pct(g.reach)} of games at {bandLabel(band) || "your level"} · {colour(g.side)} scores {pct(g.score)}
        {g.white_eval ? ` · ${fmtScore(g.white_eval)}` : ""}
      </div>
      {g.tricky >= 0.05 && trap ? (
        <div className="explore-gambit-tricky">
          <span className="badge trap">Tricky</span> {pct(g.tricky)} of the replies at your level lose. Most often{" "}
          <b>
            {plyLabel(g.from_ply + g.sans.length)} {trap.san}?
          </b>{" "}
          ({pct(trap.share)})
          {combo ? (
            <>
              , and <b>{combo.sans.join(" ")}</b> {wins(combo)}
              {motif ? ` — a ${humanTag(motif)}` : ""}
            </>
          ) : null}
          .
        </div>
      ) : (
        <div className="explore-gambit-tricky faint">Few of the replies it gets lose outright: it is played for the attack, not the trap.</div>
      )}
      <div className="explore-gambit-actions">
        <button type="button" className="btn btn-sm" onClick={onShow}>
          <Icon name="play" />
          Show
        </button>
        <button type="button" className="btn btn-sm" onClick={() => navigate(`/openings/learn?line=${encodeURIComponent(g.name)}&as=${g.side}`)}>
          <Icon name="book" />
          Study
        </button>
      </div>
    </div>
  );
}
